/**
 * AssessmentNav — fixed top navigation bar for the assessment phase
 *
 * Contains: employee info, level badge,
 * framework/feedback sidebar buttons, and milestone stepper tabs.
 * Back-to-employees button lives in the bottom nav of the content area.
 */
import { C } from '../../data/managerReviewData';
import type { Employee, ActiveTab, ActiveSidebar } from '../../types/managerReview';

interface AssessmentNavProps {
  employee: Employee;
  level: string;
  activeTab: ActiveTab;
  setActiveTab: (tab: ActiveTab) => void;
  activeSidebar: ActiveSidebar;
  setActiveSidebar: (s: ActiveSidebar) => void;
  deliveryDone: boolean;
  behaviorsDone: boolean;
  summaryDone: boolean;
  draftStatus: 'Draft' | 'Submitted' | null;
  lastSavedAt: string | null;
  isSaving: boolean;
  saveError: boolean;
}

function formatSavedAt(iso: string | null): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return `${String(d.getDate()).padStart(2,'0')}.${String(d.getMonth()+1).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  } catch { return ''; }
}

export default function AssessmentNav({
  employee,
  level,
  activeTab,
  setActiveTab,
  activeSidebar,
  setActiveSidebar,
  deliveryDone,
  behaviorsDone,
  summaryDone,
  draftStatus,
  lastSavedAt,
  isSaving,
  saveError,
}: AssessmentNavProps) {
  const milestones = [
    { label: 'Delivery',  tab: 0 as ActiveTab, done: deliveryDone },
    { label: 'Behaviors', tab: 1 as ActiveTab, done: behaviorsDone },
    { label: 'Summary',   tab: 2 as ActiveTab, done: summaryDone },
    { label: 'AI Review', tab: 3 as ActiveTab, done: summaryDone, isLock: !summaryDone },
  ];

  return (
    <div style={{
      position: 'sticky', top: 0,
      zIndex: 100, background: '#fff', boxShadow: '0 1px 8px rgba(0,0,0,0.1)',
    }}>
      <div style={{ padding: '0 8px' }}>
        {/* Top info row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, height: 52 }}>
          {/* Employee info — leftmost */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <div style={{
              width: 30, height: 30, borderRadius: 8, background: '#e0e0e0', color: '#555',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontWeight: 700, fontSize: 11, flexShrink: 0,
            }}>
              {employee.avatar}
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.2 }}>{employee.name}</div>
              <div style={{ fontSize: 11, color: C.muted }}>{employee.role}</div>
            </div>
          </div>

          <div style={{
            padding: '3px 9px', borderRadius: 6, background: '#f3f4f6',
            fontSize: 12, fontWeight: 700, color: '#555', flexShrink: 0,
          }}>{level}</div>

          <div style={{ flex: 1 }} />

          {/* Save status indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            {isSaving ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#d1d5db' }} />
                <span style={{ fontSize: 11, color: C.muted }}>Saving…</span>
              </div>
            ) : saveError ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                title="Your changes couldn't reach the server right now — this is usually a temporary connection issue. Everything you've entered is safely stored in this browser tab and will sync automatically once the connection is restored. Keep this tab open until the warning clears.">
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#e8a020', flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: C.muted }}>Stored locally</span>
              </div>
            ) : lastSavedAt ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#26bd59', flexShrink: 0 }} />
                <span style={{ fontSize: 11, color: C.muted }}>
                  {draftStatus === 'Submitted' ? 'Submitted' : 'Saved'} {formatSavedAt(lastSavedAt)}
                </span>
              </div>
            ) : null}
            {draftStatus && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 4,
                background: draftStatus === 'Submitted' ? 'rgba(38,189,89,0.1)' : 'rgba(0,135,235,0.08)',
                color: draftStatus === 'Submitted' ? '#1a7a38' : C.blue,
                border: `1px solid ${draftStatus === 'Submitted' ? 'rgba(38,189,89,0.3)' : 'rgba(0,135,235,0.2)'}`,
                textTransform: 'uppercase', letterSpacing: '0.04em',
              }}>
                {draftStatus}
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
            {([['framework', '📋 Framework'], ['feedback', '💬 Multi-Source Feedback']] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setActiveSidebar(activeSidebar === key ? null : key)}
                style={{
                  padding: '5px 12px', borderRadius: 6, fontSize: 12, fontWeight: 600,
                  cursor: 'pointer', fontFamily: 'Inter', transition: 'all 0.15s',
                  border: `1.5px solid ${activeSidebar === key ? C.blue : '#ddd'}`,
                  background: activeSidebar === key ? C.lightBlue : '#fafafa',
                  color: activeSidebar === key ? C.blue : '#777',
                }}
              >{label}</button>
            ))}
          </div>
        </div>

        {/* Flat tab bar */}
        <div style={{
          display: 'flex', alignItems: 'stretch',
          height: 40, borderTop: '1px solid #f5f5f5', gap: 0,
        }}>
          {milestones.map((m) => {
            const isActive = activeTab === m.tab;
            const isLocked = m.isLock;
            const dotColor = m.done ? '#26bd59' : isActive ? C.blue : '#e0e0e0';
            const labelColor = isLocked ? '#bbb' : m.done ? '#1a9e46' : isActive ? C.blue : '#aaa';
            const borderColor = m.done ? '#26bd59' : isActive ? C.blue : 'transparent';

            return (
              <button
                key={m.tab}
                onClick={() => { if (!isLocked) setActiveTab(m.tab); }}
                title={isLocked ? 'Complete Delivery, Behaviors, and Summary to unlock AI Review' : undefined}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '0 18px',
                  border: 'none', borderBottom: `2px solid ${borderColor}`,
                  background: 'transparent',
                  cursor: isLocked ? 'not-allowed' : 'pointer',
                  fontFamily: 'Inter', flexShrink: 0,
                  fontSize: 12, fontWeight: isActive ? 700 : 600,
                  color: labelColor,
                  opacity: isLocked ? 0.38 : 1,
                  transition: 'color 0.15s, border-color 0.15s',
                }}
              >
                <div style={{
                  width: 6, height: 6, borderRadius: '50%',
                  background: dotColor, flexShrink: 0, transition: 'background 0.2s',
                }} />
                {isLocked ? `🔒 ${m.label}` : m.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
